// src/hooks/use-speech.ts
'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

export type SpeechStatus = 'idle' | 'speaking' | 'paused'

/**
 * Thin controller over the browser's on-device Web Speech API (#791). Speaks an
 * array of sentences as a queue of short utterances (per-sentence, which both
 * gives reliable per-sentence progress via `index` for read-along highlighting
 * AND dodges the Chrome long-utterance pause bug). All on-device — no network,
 * no cost. Cancels on unmount. Returns `supported: false` where TTS is absent.
 */
export function useSpeech() {
  const supported =
    typeof window !== 'undefined' &&
    'speechSynthesis' in window &&
    typeof window.SpeechSynthesisUtterance !== 'undefined'

  const [status, setStatus] = useState<SpeechStatus>('idle')
  const [index, setIndex] = useState(-1) // sentence currently being spoken; -1 = none
  const [rate, setRateState] = useState(1)
  const rateRef = useRef(1)
  const sentencesRef = useRef<string[]>([])

  const speakFrom = useCallback(
    (sentences: string[], from: number) => {
      if (!supported) return
      const synth = window.speechSynthesis
      synth.cancel()
      sentencesRef.current = sentences
      if (sentences.length === 0) {
        setStatus('idle')
        setIndex(-1)
        return
      }
      for (let i = Math.max(0, from); i < sentences.length; i++) {
        const u = new SpeechSynthesisUtterance(sentences[i])
        u.rate = rateRef.current
        u.onstart = () => {
          setIndex(i)
          setStatus('speaking')
        }
        if (i === sentences.length - 1) {
          u.onend = () => {
            setStatus('idle')
            setIndex(-1)
          }
        }
        synth.speak(u)
      }
    },
    [supported],
  )

  const speak = useCallback((sentences: string[]) => speakFrom(sentences, 0), [speakFrom])

  const pause = useCallback(() => {
    if (!supported) return
    window.speechSynthesis.pause()
    setStatus('paused')
  }, [supported])

  const resume = useCallback(() => {
    if (!supported) return
    window.speechSynthesis.resume()
    setStatus('speaking')
  }, [supported])

  const stop = useCallback(() => {
    if (!supported) return
    window.speechSynthesis.cancel()
    setStatus('idle')
    setIndex(-1)
  }, [supported])

  const setRate = useCallback(
    (r: number) => {
      rateRef.current = r
      setRateState(r)
      // Live speed change: re-speak from the current sentence at the new rate.
      if (status === 'speaking' && sentencesRef.current.length > 0) {
        speakFrom(sentencesRef.current, Math.max(0, index))
      }
    },
    [status, index, speakFrom],
  )

  useEffect(() => {
    return () => {
      if (supported) window.speechSynthesis.cancel()
    }
  }, [supported])

  return { supported, status, index, rate, speak, pause, resume, stop, setRate }
}
